import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';
import { api, onAuthLost, refreshSession, setAccessToken } from './api';
import { navigate } from './router';

export interface User { id: string; email: string; name: string | null; locale: string | null; isPlatformAdmin: boolean; mfaEnabled: boolean; emailVerified: boolean }
export interface Membership {
  id: string; slug: string; name: string; branding: { primaryColor?: string; logoUrl?: string }; status: string; country: string;
  currency: string; timezone: string; defaultLocale: string; roles: string[]; personId: string | null; locationIds: string[] | null;
}
export interface SessionPayload { accessToken?: string; user?: User; tenants?: Membership[] }

interface AuthValue {
  ready: boolean;
  user: User | null;
  tenants: Membership[];
  login(email: string, password: string, totp?: string): Promise<void>;
  startSession(s: SessionPayload): Promise<void>;
  logout(): Promise<void>;
  reload(): Promise<void>;
}

export const STAFF_ROLES = ['owner', 'manager', 'desk', 'finance'];
export const isStaff = (m?: Membership | null): boolean => Boolean(m?.roles.some((r) => STAFF_ROLES.includes(r)));
export const isTrainer = (m?: Membership | null): boolean => Boolean(m?.roles.includes('trainer'));
export const hasRole = (m: Membership | null | undefined, ...roles: string[]): boolean => Boolean(m?.roles.some((r) => roles.includes(r)));
export const homeFor = (m: Membership): string =>
  (isStaff(m) ? `/g/${m.slug}/admin` : isTrainer(m) ? `/g/${m.slug}/trainer` : `/g/${m.slug}/planner`);

const AuthContext = createContext<AuthValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<{ ready: boolean; user: User | null; tenants: Membership[] }>({ ready: false, user: null, tenants: [] });

  const reload = useCallback(async () => {
    const me = await api<{ user: User; tenants: Membership[] }>('/v1/me');
    setState({ ready: true, user: me.user, tenants: me.tenants });
  }, []);

  useEffect(() => {
    refreshSession().then(async (ok) => {
      if (ok) {
        try { await reload(); return; } catch { /* fall through */ }
      }
      setState((s) => ({ ...s, ready: true }));
    });
  }, [reload]);

  useEffect(() => onAuthLost(() => setState({ ready: true, user: null, tenants: [] })), []);

  const startSession = useCallback(async (s: SessionPayload) => {
    if (s.accessToken) setAccessToken(s.accessToken);
    if (s.user && s.tenants) setState({ ready: true, user: s.user, tenants: s.tenants });
    else await reload();
  }, [reload]);

  const login = useCallback(async (email: string, password: string, totp?: string) => {
    const r = await api<SessionPayload>('/v1/auth/login', { body: { email, password, totp: totp || undefined } });
    await startSession(r);
  }, [startSession]);

  const logout = useCallback(async () => {
    await api('/v1/auth/logout', { body: {} }).catch(() => undefined);
    setAccessToken(null);
    setState({ ready: true, user: null, tenants: [] });
    navigate('/login');
  }, []);

  return <AuthContext.Provider value={{ ...state, login, startSession, logout, reload }}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthValue {
  const v = useContext(AuthContext);
  if (!v) throw new Error('AuthProvider missing');
  return v;
}
